
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import GameSelector from "@/components/games/GameSelector";
import P5GameContainer from "@/components/games/P5GameContainer";
import { GameGeneratorDialog } from "@/components/games/GameGeneratorDialog";
import { Game, GameSession } from "@/types/games";
import { gameService } from "@/services/gameService";
import Seo from "@/components/seo/Seo";

const Games = () => {
  const { grade } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  console.log('Games: Component rendered with grade:', grade, 'user:', user?.id);

  const handleGameSelect = async (game: Game) => {
    console.log('Games: Game selected:', {
      gameId: game.id,
      gameName: game.name,
      gameType: game.game_type,
      userId: user?.id,
    });

    setIsCreatingSession(true);
    try {
      let session: GameSession;
      if (user) {
        session = await gameService.createGameSession(
          game.id,
          grade || "1",
          game.game_type,
          user.id,
        );
      } else {
        // Guest play: synthesize a local session so the game launches
        // without persisting analytics. Sign up to save progress.
        session = {
          id: `guest-${crypto.randomUUID()}`,
          user_id: "guest",
          game_id: game.id,
          grade: grade || "1",
          subject: game.game_type,
          difficulty_level: 1,
          session_data: {},
          score: 0,
          max_score: 0,
          time_spent: 0,
          problems_attempted: 0,
          problems_correct: 0,
          hints_used: 0,
          completed: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      }

      setSelectedGame(game);
      setGameSession(session);

      toast({
        title: "Game Ready!",
        description: user
          ? `Starting ${game.name}`
          : `Starting ${game.name}. Sign up to save your scores.`,
      });
    } catch (error) {
      console.error('Games: Error creating game session:', error);
      toast({
        title: "Error",
        description: "Failed to start game. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingSession(false);
    }
  };

  const handleGameComplete = async (completedSession: GameSession) => {
    console.log('Games: Game completed:', completedSession);

    try {
      if (user && !completedSession.id.startsWith("guest-")) {
        await gameService.updateGameSession(completedSession.id, {
          score: completedSession.score,
          time_spent: completedSession.time_spent,
          completed: true,
        });
      }

      toast({
        title: "Game Completed! 🎉",
        description: user
          ? `Final Score: ${completedSession.score}`
          : `Final Score: ${completedSession.score}. Sign up to save it!`,
      });

      setSelectedGame(null);
      setGameSession(null);
    } catch (error) {
      console.error('Games: Error updating game session:', error);
      toast({
        title: "Error",
        description: "Failed to save game progress.",
        variant: "destructive",
      });
    }
  };

  const handleExitGame = () => {
    console.log('Games: Exiting game, returning to selector');
    setSelectedGame(null);
    setGameSession(null);
  };

  // If game is selected, show the game container
  if (selectedGame && gameSession) {
    console.log('Games: Rendering P5GameContainer with:', {
      gameName: selectedGame.name,
      sessionId: gameSession.id
    });
    
    return (
      <P5GameContainer
        game={selectedGame}
        gameSession={gameSession}
        onComplete={handleGameComplete}
        onExit={handleExitGame}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex flex-col">
      <Seo
        title={grade ? `Grade ${grade} Learning Games` : 'Interactive Learning Games for Grades 1–9'}
        description="Play fun, interactive learning games for math, reading, and writing — designed for grades 1 through 9."
        canonicalPath={grade ? `/games/${grade}` : '/games'}
      />
      <div className="flex-1 container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h1 className="text-2xl font-bold text-gray-800">
              Interactive Learning Games - Grade {grade}
            </h1>
            <GameGeneratorDialog
              grade={grade || "1"}
              onGameReady={(g) => handleGameSelect(g)}
            />
          </div>

          {isCreatingSession ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2">Starting your game...</span>
            </div>
          ) : (
            <GameSelector
              grade={grade || "1"}
              onGameSelect={handleGameSelect}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Games;
